import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Lock, RotateCcw, Save, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  permissoesService, CatalogoPermissoes, PerfilPermissao,
} from '../../services/permissoes';
import { useConfirm } from '../../components/ConfirmDialog';

/**
 * Configuração das permissões por perfil.
 *
 * A grade é módulo por linha e ação por coluna, com as ações fixas (consultar,
 * criar, editar, excluir) sempre nas mesmas posições. As ações próprias de cada
 * módulo, como dar baixa no financeiro, vêm depois. Manter a posição fixa deixa
 * a leitura vertical possível: dá para varrer a coluna "Excluir" e ver de uma
 * vez tudo que o perfil pode apagar.
 */

const ACOES_FIXAS = ['consultar', 'criar', 'editar', 'excluir'];

const PermissoesList: React.FC = () => {
  const confirm = useConfirm();
  const [catalogo, setCatalogo] = useState<CatalogoPermissoes | null>(null);
  const [perfilAtivo, setPerfilAtivo] = useState('operador');
  const [rascunho, setRascunho] = useState<Record<string, boolean>>({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await permissoesService.catalogo();
      setCatalogo(res.data);
      const perfil = res.data.perfis.find((p) => p.role === perfilAtivo) || res.data.perfis[0];
      setRascunho({ ...perfil.permissoes });
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar as permissões.');
    } finally {
      setCarregando(false);
    }
  }, [perfilAtivo]);

  useEffect(() => { carregar(); }, [carregar]);

  const perfil: PerfilPermissao | undefined = useMemo(
    () => catalogo?.perfis.find((p) => p.role === perfilAtivo),
    [catalogo, perfilAtivo],
  );

  // Comparar com o que veio do servidor evita habilitar "Salvar" quando a pessoa
  // marca e desmarca a mesma caixa, voltando ao estado original.
  const alterado = useMemo(() => {
    if (!perfil) return false;
    return Object.keys(rascunho).some((k) => rascunho[k] !== perfil.permissoes[k]);
  }, [rascunho, perfil]);

  const travada = (chave: string) =>
    perfilAtivo === 'admin' && (catalogo?.sempre_do_admin || []).includes(chave);

  const alternar = (chave: string) => {
    if (travada(chave)) return;
    setRascunho((atual) => ({ ...atual, [chave]: !atual[chave] }));
  };

  const marcarModulo = (modulo: string, acoes: string[], valor: boolean) => {
    setRascunho((atual) => {
      const novo = { ...atual };
      acoes.forEach((acao) => {
        const chave = `${modulo}.${acao}`;
        if (!travada(chave)) novo[chave] = valor;
      });
      return novo;
    });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const res = await permissoesService.salvar(perfilAtivo, rascunho);
      toast.success(res.message);
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const restaurar = async () => {
    if (!(await confirm({
      title: 'Restaurar o padrão',
      message: `As permissões do perfil ${perfil?.rotulo} voltam ao padrão do sistema e as marcações feitas por você são descartadas.`,
      confirmLabel: 'Restaurar',
      variant: 'danger',
    }))) return;
    try {
      const res = await permissoesService.restaurarPadrao(perfilAtivo);
      toast.success(res.message);
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Não foi possível restaurar.');
    }
  };

  if (carregando) {
    return <div className="p-10 text-center text-gray-500">Carregando...</div>;
  }

  if (erro || !catalogo || !perfil) {
    return (
      <div role="alert" className="bg-white border rounded-lg p-8 text-center">
        <p className="text-red-600 mb-3">{erro || 'Permissões indisponíveis.'}</p>
        <button onClick={carregar} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Tentar novamente
        </button>
      </div>
    );
  }

  // Ações extras são as que o módulo tem além das quatro fixas.
  const extrasDe = (acoes: string[]) => acoes.filter((a) => !ACOES_FIXAS.includes(a));

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Permissões</h1>
          </div>
          <p className="text-gray-600 mt-1">
            Marque o que cada perfil pode fazer em cada módulo. Vale para todos os usuários
            daquele perfil na sua empresa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={restaurar}
            disabled={!perfil.personalizado}
            title={perfil.personalizado ? 'Voltar ao padrão do sistema' : 'Este perfil já está no padrão'}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCcw className="h-4 w-4" /> Restaurar padrão
          </button>
          <button
            onClick={salvar}
            disabled={!alterado || salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" /> {salvando ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>

      {/* Perfis */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Perfis">
          {catalogo.perfis.map((p) => (
            <button
              key={p.role}
              onClick={() => setPerfilAtivo(p.role)}
              aria-current={p.role === perfilAtivo ? 'page' : undefined}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                p.role === perfilAtivo
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {p.rotulo}
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                <Users className="h-3 w-3" />{p.usuarios}
              </span>
            </button>
          ))}
        </nav>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
          perfil.personalizado ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
          {perfil.personalizado ? 'Configurado pela empresa' : 'Padrão do sistema'}
        </span>
        {alterado && (
          <span className="text-amber-700">Há alterações não salvas neste perfil.</span>
        )}
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold min-w-[15rem]">Módulo</th>
                {ACOES_FIXAS.map((acao) => (
                  <th key={acao} className="px-3 py-3 font-semibold text-center whitespace-nowrap">
                    {catalogo.acoes[acao]}
                  </th>
                ))}
                <th className="text-left px-4 py-3 font-semibold">Ações do módulo</th>
                <th className="px-3 py-3 font-semibold text-center whitespace-nowrap">Tudo</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {Object.entries(catalogo.modulos).map(([modulo, def]) => {
                const extras = extrasDe(def.acoes);
                const todasMarcadas = def.acoes.every((a) => rascunho[`${modulo}.${a}`]);
                return (
                  <tr key={modulo} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{def.rotulo}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{def.descricao}</div>
                    </td>

                    {ACOES_FIXAS.map((acao) => {
                      const chave = `${modulo}.${acao}`;
                      const existe = def.acoes.includes(acao);
                      return (
                        <td key={acao} className="px-3 py-3 text-center">
                          {existe ? (
                            <label className="inline-flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(rascunho[chave])}
                                onChange={() => alternar(chave)}
                                disabled={travada(chave)}
                                aria-label={`${catalogo.acoes[acao]} em ${def.rotulo}`}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                              />
                              {travada(chave) && <Lock className="h-3 w-3 text-gray-400 ml-1" />}
                            </label>
                          ) : (
                            <span className="text-gray-300" aria-hidden>—</span>
                          )}
                        </td>
                      );
                    })}

                    <td className="px-4 py-3">
                      {extras.length === 0 ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                          {extras.map((acao) => {
                            const chave = `${modulo}.${acao}`;
                            return (
                              <label key={acao} className="inline-flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={Boolean(rascunho[chave])}
                                  onChange={() => alternar(chave)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">{catalogo.acoes[acao]}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={todasMarcadas}
                        onChange={() => marcarModulo(modulo, def.acoes, !todasMarcadas)}
                        aria-label={`Marcar tudo em ${def.rotulo}`}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        O administrador nunca perde o acesso a usuários e a esta tela, por isso essas caixas
        aparecem travadas. Sem essa reserva, desmarcá-las trancaria a empresa para fora da
        própria configuração.
      </p>
    </div>
  );
};

export default PermissoesList;
